import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '~/components';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import { useMsaasListingSearchRef, useNegotiationRef } from '../../marketSelectors';
import { NegotiationDetails } from '../../negotiations/NegotiationDetails';
import { useSearchSide } from '../searchSide';
import { ApplyModal } from './ApplyModal';
import { AssociatedProviderPicker } from './AssociatedProviderPicker';
import { MessageModal } from './MessageModal';
import { OfferingRequestEditor } from './OfferingRequestEditor';

export const ActionModal = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  if (!msaasListingsSearchRef) return null;

  return <ActionModalContent msaasListingsSearchRef={msaasListingsSearchRef} />;
};

type MsaasListingsSearchRef = NonNullable<
  ReturnType<typeof useMsaasListingSearchRef>['msaasListingsSearchRef']
>;

const selectState = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) => snapshot.value;
const selectEmployerId = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.employerUser?.employerId;
const TAGS = CT.listingSearch;

const selectListingId = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.listing?.id;
const selectModalOpen = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.modalOpen;
const selectLoading = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.loading;
const selectMessageDraft = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.messageDraft;
const selectExistingInquiry = (snapshot: ReturnType<MsaasListingsSearchRef['getSnapshot']>) =>
  snapshot.context.existingInquiryResponse;

const ActionModalContent = ({
  msaasListingsSearchRef,
}: {
  msaasListingsSearchRef: MsaasListingsSearchRef;
}) => {
  const side = useSearchSide();
  const state = useSelector(msaasListingsSearchRef, selectState);
  const employerId = useSelector(msaasListingsSearchRef, selectEmployerId);
  const listingId = useSelector(msaasListingsSearchRef, selectListingId);
  const modalOpen = useSelector(msaasListingsSearchRef, selectModalOpen);
  const loading = useSelector(msaasListingsSearchRef, selectLoading);
  const messageDraft = useSelector(msaasListingsSearchRef, selectMessageDraft);
  const existingInquiry = useSelector(msaasListingsSearchRef, selectExistingInquiry);
  const { negotiationRef } = useNegotiationRef();
  const submitLifecycleBusy = useSelector(
    negotiationRef,
    (snapshot) => snapshot?.context.submitLifecycleBusy ?? false
  );
  const [providerIds, setProviderIds] = useState<number[]>([]);

  useEffect(() => {
    setProviderIds([]);
  }, [modalOpen]);

  const closeDisabled = state === '$_NEGOTIATE' && submitLifecycleBusy;
  const handleClose = () => {
    const currentState = msaasListingsSearchRef.getSnapshot().value;
    // Already closed (e.g. the machine closed itself after a submitted offer).
    if (currentState === '$_VIEW') return;
    const negotiationBusy = negotiationRef?.getSnapshot().context.submitLifecycleBusy ?? false;
    if (currentState !== '$_NEGOTIATE' || !negotiationBusy) {
      msaasListingsSearchRef.send({ type: 'ON_CLOSE_MODAL' });
    }
  };

  return (
    <ModalDialog
      open={modalOpen}
      onClose={handleClose}
      closeDisabled={closeDisabled}
      xButton={true}
    >
      {loading && <LoadingSpinner cyTag={TAGS.detailPanel.modalSpinner} />}
      {state === '$_APPLY' && <ApplyModal />}
      {state === '$_NEGOTIATE' && listingId != null && side === 'OFFERING' && (
        <OfferingRequestEditor
          listingId={listingId}
          onSubmitted={(jobResponse) =>
            msaasListingsSearchRef.send({
              type: 'ON_NEGOTIATION_SUBMITTED',
              payload: { jobResponse },
            })
          }
          onClose={handleClose}
        />
      )}
      {state === '$_NEGOTIATE' && listingId != null && side === 'SEEKING' && (
        <>
          <p>
            You are about to begin negotiating the terms of this position. You may negotiate for
            multiple rounds, but the final terms must be accepted by both parties. You will control
            final approval before agreeing to the terms and conditions.
          </p>
          <AssociatedProviderPicker
            employerId={employerId}
            selectedProviderIds={providerIds}
            onChange={setProviderIds}
          />
          {/* [MCV-1646] draft-first initial offer: the editor loads a draft
              scoped to this listing and nothing is created server-side until
              its own Submit Offer button is clicked. There is no separate
              footer Submit button here anymore. */}
          <NegotiationDetails
            draftSource={{ listingType: 'MSAAS', listingId }}
            draftProviderIds={providerIds}
            viewerRole="EMPLOYER"
            onDraftSubmitted={(result) =>
              msaasListingsSearchRef.send({
                type: 'ON_NEGOTIATION_SUBMITTED',
                payload: { jobResponse: result.jobResponse },
              })
            }
            onClose={handleClose}
          />
        </>
      )}
      {state === '$_MESSAGE' && (
        <MessageModal initialMessage={messageDraft} existingInquiry={existingInquiry} />
      )}
    </ModalDialog>
  );
};
